import { useState, useEffect } from "react"
import * as React from "react"
import { Box, NativeSelect, Text, VStack, HStack, Button } from "@chakra-ui/react"
import Dialog from "@hydrologiq/shared/chakra/Dialog"
import { useActiveOrganization, useOrganizations } from "../../auth/AuthProvider"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { CancelButton, ConfirmButton } from "@hydrologiq/shared/content/components/Button"
import { NativeSelectField, NativeSelectRoot } from "@hydrologiq/shared/chakra/NativeSelect"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { clearSections } from "../../state/sectionSlice"

interface OrganizationSwitchDialogProps {
  open: boolean
  onClose: () => void
}

/**
 * OrganizationSwitchDialog - Dialog for switching between organizations
 * Shows a native select dropdown with all available organizations
 */
export const OrganizationSwitchDialog = ({ open, onClose }: OrganizationSwitchDialogProps) => {
  const { activeOrganization, switchOrganization } = useActiveOrganization()
  const { organizations } = useOrganizations()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  // Initialise with active org if available, otherwise first org, otherwise empty
  const initialOrgId = activeOrganization?.organization.id || (organizations.length > 0 ? organizations[0].id : "")
  const [selectedOrgId, setSelectedOrgId] = useState(initialOrgId)
  const [switching, setSwitching] = useState(false)

  // Update selectedOrgId when dialog opens or organizations change
  React.useEffect(() => {
    if (open) {
      const newOrgId = activeOrganization?.organization.id || (organizations.length > 0 ? organizations[0].id : "")
      setSelectedOrgId(newOrgId)
    }
  }, [open, activeOrganization, organizations])

  const handleSwitch = async () => {
    if (!selectedOrgId) {
      toaster.error({
        title: "No organization selected",
        description: "Please select an organization",
      })
      return
    }

    // If selecting the same org that's already active, just close
    if (selectedOrgId === activeOrganization?.organization.id) {
      onClose()
      return
    }

    setSwitching(true)
    try {
      await switchOrganization(selectedOrgId)
      dispatch(clearSections())
      toaster.success({
        title: "Organization switched",
        description: "You have switched to a different organization",
      })
      onClose()
      navigate("/")
    } catch (error: any) {
      toaster.error({
        title: "Failed to switch organization",
        description: error.message || "Unknown error",
      })
    } finally {
      setSwitching(false)
    }
  }

  return (
    <Dialog
      title="Change Organisation"
      open={open}
      setOpen={onClose}
      loading={switching}
      disabled={switching}
      footer={
        <HStack>
          <CancelButton onClick={onClose} disabled={switching} />
          <ConfirmButton
            context={{ label: "Switch Organization", description: "Switch to the selected organization" }}
            onClick={handleSwitch}
            loading={switching}
            disabled={switching}
          />
        </HStack>
      }
    >
      <VStack gap={4} align="stretch" width="full">
        <Box>
          <Text fontSize="sm" mb={2}>
            Select Organisation
          </Text>
          <NativeSelectRoot>
            <NativeSelectField
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              items={[
                ...organizations.map((org) => ({
                  label: org.name,
                  value: org.id,
                })),
              ]}
              placeholder="Select an organization..."
            />
          </NativeSelectRoot>
        </Box>
        {organizations && organizations.length === 0 && (
          <Text fontSize="sm" color="gray.500">
            You don't have access to any organizations.
          </Text>
        )}
      </VStack>
    </Dialog>
  )
}
